import { useCallback, useEffect, useRef, useState } from 'react';

// Helper function to get seconds remaining until expiry (countdown)
const getSecondsRemaining = (expiryTimeEpoch: number): number => {
	const currentTimeEpoch = Date.now();
	return Math.max((expiryTimeEpoch - currentTimeEpoch) / 1000, 0);
};

// Helper function to get total elapsed seconds (count up)
const getSecondsElapsed = (
	startTimeEpoch: number,
	baselineElapsedMs: number,
): number => {
	const currentTime = Date.now();
	const hookElapsedMs = currentTime - startTimeEpoch;
	return (baselineElapsedMs + hookElapsedMs) / 1000;
};

type CountUpOptions = {
	direction: 'up';
	elapsedTimeMs: number; // Starting elapsed time in ms
	stopTimer?: boolean; // Flag to stop the timer
};

type CountDownOptions = {
	direction: 'down';
	durationMs: number; // Duration in milliseconds to count down from
	onExpire?: () => void;
};

export type UseTimerOptions = CountUpOptions | CountDownOptions;

/**
 * Hook for managing countdown timer or elapsed time counter
 *
 * @param options - Configuration options for the timer
 * @returns Seconds remaining (countdown) or seconds elapsed (count up)
 *
 */
export const useTimer = (options: UseTimerOptions): number => {
	const { direction } = options;

	// Type-safe destructuring based on direction
	const isCountUp = direction === 'up';
	const elapsedTimeMs = isCountUp
		? (options as CountUpOptions).elapsedTimeMs
		: 0;
	const stopTimer = isCountUp ? (options as CountUpOptions).stopTimer : false;
	const durationMs = !isCountUp ? (options as CountDownOptions).durationMs : 0;
	const onExpire = !isCountUp
		? (options as CountDownOptions).onExpire
		: undefined;

	// Use ref to store stable start time for both count-up and count-down
	const countUpStartTimeRef = useRef<number>(Date.now());
	const countDownStartTimeRef = useRef<number>(Date.now());

	const startTimeMs = isCountUp
		? 0 // For count-up, startTimeMs is 0
		: countDownStartTimeRef.current;

	// Validate inputs based on direction
	const isValid =
		!stopTimer &&
		(isCountUp
			? elapsedTimeMs >= 0 // For count-up, only check elapsedTimeMs
			: durationMs > 0); // For count-down, check durationMs

	const expiryTime = !isCountUp ? startTimeMs + durationMs : 0; // Calculate expiry time from start + duration

	const [seconds, setSeconds] = useState(() => {
		if (!isValid) return 0;
		return isCountUp ? elapsedTimeMs / 1000 : getSecondsRemaining(expiryTime);
	});

	// Reset seconds when key parameters change
	useEffect(() => {
		if (!isValid) {
			setSeconds(0);
			return;
		}

		// Reset start time when parameters change
		if (isCountUp) {
			countUpStartTimeRef.current = Date.now();
		} else {
			countDownStartTimeRef.current = Date.now();
		}

		const initialSeconds = isCountUp
			? elapsedTimeMs / 1000
			: getSecondsRemaining(expiryTime);

		setSeconds(initialSeconds);
	}, [isValid, expiryTime, elapsedTimeMs, isCountUp]);

	const updateTimer = useCallback(() => {
		if (!isValid) return;

		if (isCountUp) {
			const elapsed = getSecondsElapsed(
				countUpStartTimeRef.current,
				elapsedTimeMs,
			);
			setSeconds(elapsed);
		} else {
			const remaining = getSecondsRemaining(expiryTime);
			setSeconds(remaining);
			// Handle expiry for countdown
			if (remaining <= 0) {
				onExpire?.();
			}
		}
	}, [isCountUp, expiryTime, onExpire, isValid, elapsedTimeMs]);

	useEffect(() => {
		// Don't start timer for invalid inputs or if stopTimer is true
		if (!isValid || stopTimer) {
			return;
		}
		const intervalId = setInterval(updateTimer, 1000);
		// Immediate cleanup if already expired (countdown only)
		if (!isCountUp && seconds <= 0) {
			clearInterval(intervalId);
			return;
		}

		return () => clearInterval(intervalId);
	}, [updateTimer, seconds, isValid, isCountUp, stopTimer]);

	return seconds;
};
